import valid from 'card-validator';
import * as Yup from 'yup';

const _POSTAL_CODE_ERROR = 'Postal code is required';
const EXPIRATION_DATE_REGEX = /(0[1-9]|10|11|12)\/[0-9]{2}$/;
const CVV_REGEX = /^[0-9]{3,4}$/;

function schema({ paymentRequired = true, shippingRequired = true } = {}) {
  return Yup.object({
    cartId: Yup.string().nullable(true),
    acceptedTerms: Yup.boolean().required(
      'You must accept the terms and conditions to continue',
    ),
    deliveryMethod: Yup.string().required('Delivery method is required'),
    creditCard: Yup.object()
      .shape({
        id: Yup.string().nullable(true),
        nameOnCard: Yup.string().nullable(),
        cardNumber: Yup.string().nullable(),
        expirationDate: Yup.string().nullable(),
        cvv: Yup.string().nullable(),
      })
      .test(
        'credit-card-validation',
        'Either card ID or all card details are required',
        function (value) {
          if (!paymentRequired) {
            return true;
          }

          const { id, nameOnCard, cardNumber, expirationDate, cvv } =
            value || {};
          const hasId = id && id.trim() !== '' && id !== 'new-card';
          const hasAllFields =
            nameOnCard && cardNumber && expirationDate && cvv;

          if (hasId) {
            return true;
          }

          if (!hasAllFields) {
            return this.createError({
              path: 'creditCard',
              message: 'Either select a saved card or enter all card details',
            });
          }

          return true;
        },
      )
      .test(
        'card-number-validation',
        'Credit Card number is invalid',
        (value) => {
          if (!paymentRequired) {
            return true;
          }

          const { id, cardNumber } = value || {};
          if (id && id !== 'new-card') {
            return true;
          }
          if (!cardNumber) {
            return true;
          }
          return valid.number(cardNumber).isValid;
        },
      )
      .test(
        'card-number-length',
        'Credit Card number must be between 13 and 19 digits',
        (value) => {
          if (!paymentRequired) {
            return true;
          }

          const { id, cardNumber } = value || {};
          if (id && id !== 'new-card') {
            return true;
          }
          if (!cardNumber) {
            return true;
          }
          return cardNumber.length >= 13 && cardNumber.length <= 19;
        },
      )
      .test(
        'expiration-date-validation',
        'Expiration Date is invalid',
        (value) => {
          if (!paymentRequired) {
            return true;
          }

          const { id, expirationDate } = value || {};
          if (id && id !== 'new-card') {
            return true;
          }
          if (!expirationDate) {
            return true;
          }
          return valid.expirationDate(expirationDate).isValid;
        },
      )
      .test(
        'expiration-date-format',
        'Must be in the format MM/YY',
        (value) => {
          if (!paymentRequired) {
            return true;
          }

          const { id, expirationDate } = value || {};
          if (id && id !== 'new-card') {
            return true;
          }
          if (!expirationDate) {
            return true;
          }
          return EXPIRATION_DATE_REGEX.test(expirationDate);
        },
      )
      .test('cvv-validation', 'CVV number is invalid', (value) => {
        if (!paymentRequired) {
          return true;
        }

        const { id, cvv } = value || {};
        if (id && id !== 'new-card') {
          return true;
        }
        if (!cvv) {
          return true;
        }
        return valid.cvv(cvv, [3, 4]).isValid;
      })
      .test('cvv-format', 'CVV Must only be 3 or 4 digits', (value) => {
        if (!paymentRequired) {
          return true;
        }

        const { id, cvv } = value || {};
        if (id && id !== 'new-card') {
          return true;
        }
        if (!cvv) {
          return true;
        }
        return CVV_REGEX.test(cvv);
      }),
    shippingAddress: Yup.object()
      .shape({
        id: Yup.string().nullable(true),
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(true),
        city: Yup.string().nullable(),
        region: Yup.string().nullable(),
        postalCode: Yup.string().nullable(),
        country: Yup.string().nullable(),
        isDefault: Yup.boolean(),
      })
      .test(
        'shipping-address-validation',
        'Either shipping address ID or all address fields are required',
        function (value) {
          if (!shippingRequired) {
            return true;
          }

          const { id, address, city, region, postalCode, country } =
            value || {};
          const hasId = id && id.trim() !== '' && id !== 'new-address';
          const hasAllFields =
            address && city && region && postalCode && country;

          if (hasId) {
            return true;
          }

          if (!hasAllFields) {
            return this.createError({
              path: 'shippingAddress',
              message:
                'Either select a saved shipping address or enter all address details',
            });
          }

          return true;
        },
      ),
    billingAddress: Yup.object()
      .shape({
        id: Yup.string().nullable(true),
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(true),
        city: Yup.string().nullable(),
        region: Yup.string().nullable(),
        postalCode: Yup.string().nullable(),
        country: Yup.string().nullable(),
      })
      .test(
        'billing-address-validation',
        'Either billing address ID or all address fields are required',
        function (value) {
          if (!paymentRequired) {
            return true;
          }

          const creditCardId = this.parent?.creditCard?.id;
          const hasExistingCard =
            creditCardId &&
            creditCardId.trim() !== '' &&
            creditCardId !== 'new-card';

          if (hasExistingCard) {
            return true;
          }

          const { id, address, city, region, postalCode, country } =
            value || {};
          const hasId = id && id.trim() !== '';
          const hasAllFields =
            address && city && region && postalCode && country;

          if (hasId) {
            return true;
          }

          if (!hasAllFields) {
            return this.createError({
              path: 'billingAddress',
              message:
                'Either select a saved card with billing address or enter all address details',
            });
          }

          return true;
        },
      ),
  });
}

export default schema;
